import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, filter, tap, debounceTime, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MatchingActions } from './matching.actions';
import { MatchingApiService } from '@app/core/api/matching';
import { WebSocketApiService, WSEventType } from '@app/core/api/websocket';
import { LoggerService } from '@app/core/infrastructure';
import { ToastNotificationService } from '@app/core/infrastructure';
import { AudioActions } from '@app/store/audio';
import { selectIsAuthenticated, selectUserBackendSyncCompleted } from '@app/store/auth/auth.selectors';

@Injectable()
export class MatchingEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly matchingApi = inject(MatchingApiService);
  private readonly wsService = inject(WebSocketApiService);
  private readonly toastService = inject(ToastNotificationService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService).scope('MatchingEffects');

  // ── REST effects ────────────────────────────────────────

  loadMatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MatchingActions.loadMatches),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectUserBackendSyncCompleted),
      ),
      // Gate on session AND backend sync to avoid login/logout race 404s.
      filter(([_, isAuthenticated, backendSyncCompleted]) => {
        const ready = isAuthenticated && backendSyncCompleted;
        if (!ready) {
          this.logger.debug('Skipping loadMatches - session not fully ready', {
            isAuthenticated,
            backendSyncCompleted,
          });
        }
        return ready;
      }),
      switchMap(() =>
        this.matchingApi.getMatches().pipe(
          map(matches => MatchingActions.loadMatchesSuccess({ matches })),
          catchError(error => {
            this.logger.error('Load matches failed:', error);
            return of(MatchingActions.loadMatchesFailure({ error: error.message }));
          })
        )
      )
    )
  );

  respondToMatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MatchingActions.respondToMatch),
      switchMap(({ proposalId, action }) =>
        this.matchingApi.respondToMatch(proposalId, { action }).pipe(
          switchMap(response => [
            MatchingActions.respondToMatchSuccess({ response }),
            MatchingActions.loadMatches(),
          ]),
          catchError(error => {
            this.logger.error('Respond to match failed:', error);
            return of(MatchingActions.respondToMatchFailure({ error: error.message }));
          })
        )
      )
    )
  );

  cancelMatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MatchingActions.cancelMatch),
      switchMap(({ proposalId }) =>
        this.matchingApi.cancelMatch(proposalId).pipe(
          switchMap(response => [
            MatchingActions.cancelMatchSuccess({ response }),
            MatchingActions.loadMatches(),
          ]),
          catchError(error => {
            this.logger.error('Cancel match failed:', error);
            return of(MatchingActions.cancelMatchFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // ── WS incoming events ──────────────────────────────────

  /** Generic navigate event — server tells client to go to a path */
  navigate$ = createEffect(() =>
    this.wsService.message$.pipe(
      filter(msg => msg.type === WSEventType.NAVIGATE),
      tap(msg => {
        const path = msg.payload?.path;
        if (path) {
          this.logger.debug('WS navigate →', path);
          this.router.navigateByUrl(path);
        }
      })
    ),
    { dispatch: false }
  );

  /** Incoming connection request — show toast & refresh */
  matchRequest$ = createEffect(() =>
    this.wsService.message$.pipe(
      filter(msg => msg.type === WSEventType.MATCH_REQUEST),
      tap(msg => {
        const name = msg.payload?.name;
        this.toastService.showSuccess(
          this.translate.instant('matching.ws.requestReceived', { name })
        );
      }),
      map(() => MatchingActions.loadMatches())
    )
  );

  /** Match accepted — show toast & refresh */
  matchAccepted$ = createEffect(() =>
    this.wsService.message$.pipe(
      filter(msg => msg.type === WSEventType.MATCH_ACCEPTED),
      tap(msg => {
        const name = msg.payload?.name;
        this.toastService.showSuccess(
          this.translate.instant('matching.ws.matchAccepted', { name })
        );
      }),
      map(() => MatchingActions.loadMatches())
    )
  );

  /** Match declined — show info toast & refresh */
  matchDeclined$ = createEffect(() =>
    this.wsService.message$.pipe(
      filter(msg => msg.type === WSEventType.MATCH_DECLINED),
      tap(() => {
        this.toastService.showInfo(
          this.translate.instant('matching.ws.matchDeclined')
        );
      }),
      map(() => MatchingActions.loadMatches())
    )
  );

  /** Match expired — show info toast & refresh */
  matchExpired$ = createEffect(() =>
    this.wsService.message$.pipe(
      filter(msg => msg.type === WSEventType.MATCH_EXPIRED),
      tap(() => {
        this.toastService.showInfo(
          this.translate.instant('matching.ws.matchExpired')
        );
      }),
      map(() => MatchingActions.loadMatches())
    )
  );

  /** Outbound match request sent via avatar — toast confirmation & refresh store */
  matchRequestSent$ = createEffect(() =>
    this.wsService.message$.pipe(
      filter(msg => msg.type === WSEventType.MATCH_REQUEST_SENT),
      tap(msg => {
        const name = msg.payload?.name;
        this.toastService.showSuccess(
          this.translate.instant('matching.ws.requestSent', { name })
        );
      }),
      map(() => MatchingActions.loadMatches())
    )
  );

  /** Refresh matches when conversation session ends (only if authenticated) */
  sessionEnded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AudioActions.sessionStopped),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      filter(([_, isAuthenticated]) => isAuthenticated),
      map(() => MatchingActions.loadMatches())
    )
  );
}
